import { Campaign } from 'entities/campaign';
import { Selectors as Router } from 'router';
import { Filters, Options, Sorter, Sorters } from 'services/genericAPIGatewayService';
import { State } from 'store';
import { TFunction } from 'react-i18next';

type SortKey = 'name' | 'name_reverse' | 'modified' | 'modified_reverse' | 'created' | 'created_reverse';

interface SortOption {
  key: SortKey;
  description: string;
  sorter: Sorter<any>;
}

export const SortOptions: (translator: TFunction<'campaigns'>) => SortOption[] = translator => [
  { key: 'name', description: translator('table.filters.name'), sorter: Sorters.name() },
  {
    key: 'name_reverse',
    description: translator('table.filters.nameReverse'),
    sorter: Sorters.name(true),
  },
  { key: 'modified', description: translator('table.filters.modified'), sorter: Sorters.lastModified(true) },
  { key: 'modified_reverse', description: translator('table.filters.modifiedReverse'), sorter: Sorters.lastModified() },
  { key: 'created', description: translator('table.filters.created'), sorter: Sorters.lastCreated(true) },
  { key: 'created_reverse', description: translator('table.filters.createdReverse'), sorter: Sorters.lastCreated() },
];

export const defaultSortKey: SortKey = 'modified';

const getOption = (key: string, translator: TFunction<'campaigns'>): SortOption => {
  const option = SortOptions(translator).find(opt => opt.key === key);
  return option ? option : SortOptions(translator).find(opt => opt.key === 'name');
};

export const getDescription = (key: string, translator: TFunction<'campaigns'>): string =>
  getOption(key, translator).description;

export const getSorter = (key: string, translator: TFunction<'campaigns'>): Sorter<Campaign> =>
  getOption(key, translator).sorter;

export function getOptions<T>(state: State, translator: TFunction<'campaigns'>): Options<T> {
  const options: Options<T> = { filters: [], sorter: undefined };

  const status = Router.getQuery(state, 'status');
  if (status) {
    options.filters.push(Filters.lifecycleStatus(status));
  } else {
    options.filters.push(Filters.lifecycleStatus('all'));
  }

  const search = Router.getQuery(state, 'search');
  if (search) {
    options.filters.push(Filters.nameIncludes(search));
  }

  const sort = Router.getQuery(state, 'sort');
  if (sort) {
    options.sorter = getSorter(sort, translator);
  } else {
    options.sorter = getSorter(defaultSortKey, translator);
  }

  return options;
}
